import { useEffect, useState, useContext } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { AuthContext } from "../../context/Auth/AuthContext";

import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from '@material-ui/core/TextField';
import { green } from "@material-ui/core/colors";
import { makeStyles } from "@material-ui/core/styles";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import CheckSupportEmail from "../../helpers/CheckSupportEmail";
import { Typography } from "@material-ui/core";
import SingleSelect from "../InputStyled/singleSelect";
import { Box, Grid } from "@material-ui/core";
import { useMemo } from "react";

const useStyles = makeStyles(theme => ({
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const types = [{
  name: "Receptivo",
  value: "1"
}, {
  name: "Ativo",
  value: "0"
}];

const integrationTypes = [{
  name: "Construtor padrão",
  value: 0
}, {
  name: "N8N",
  value: 1
}, {
  name: "Active Pieces",
  value: 2
}];

const ChatbotModal = ({ open, onClose, chatbotId }) => {

  const classes = useStyles();
  const { user: loggedInUser } = useContext(AuthContext);
  const isSupportUser = CheckSupportEmail(loggedInUser?.email);

  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState(null);
  const [conclusionMessages, setConclusionMessages] = useState([]);

  const initialState = {
    name: "",
    isReceptive: "1",
    typeIntegration: "0",
    integrationUrl: "",
    inactivityMinutes: "",
    inactivityAnnotationMessage: "",
    inactivityAction: "finish",
    inactivityQueueId: "",
    inactivityConclusionId: ""
  };
  const [chatbot, setChatbot] = useState(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    (async () => {
      try {
        const { data } = await api.get(`/whatsapp/`, {
          signal: abortController.signal,
        });
        if (!isMounted) return;
        setWhatsapps(data);
      } catch (err) {
        if (!isMounted || err.name == 'AbortError') return;
        console.error({ err });
        toastError(err);
      }
    })();

    (async () => {
      try {
        const { data } = await api.get(`/conclusionMessagesAll/`, {
          signal: abortController.signal,
        });
        if (!isMounted) return;
        setConclusionMessages(data.conclusionMessages);
      } catch (err) {
        if (!isMounted || err.name == 'AbortError') return;
        console.error({ err });
        toastError(err);
      }
    })();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const fetchChatbot = async () => {
      if (!chatbotId) {
        setChatbot(initialState);
        return;
      }

      try {
        const { data } = await api.get(`/chatbot/${chatbotId}`, {
          signal: abortController.signal,
        });
        if (!isMounted) return;
        setChatbot(prevState => ({
          ...prevState,
          name: data.name,
          WhatsappChatbots: data.WhatsappChatbots,
          isReceptive: data.isReceptive == true ? "1" : "0",
          typeIntegration: String(data.typeIntegration),
          integrationUrl: data.integrationUrl || "",
          inactivityMinutes: data?.inactivityMinutes || "",
          inactivityAnnotationMessage: data?.inactivityAnnotationMessage || "",
          inactivityAction: data?.inactivityAction || "",
          inactivityQueueId: data?.inactivityQueueId || "",
          inactivityConclusionId: data?.inactivityConclusionId || ""
        }));

        if (data?.whatsappId) setSelectedWhatsapp(data.whatsappId);
      } catch (err) {
        if (!isMounted || err.name == 'AbortError') return;
        console.error({ err });
        toastError(err);
      }
    };

    fetchChatbot();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [chatbotId, open]);

  const connectionQueues = useMemo(() => {
    if (!selectedWhatsapp) return [];

    const whatsapp = whatsapps.find(w => w.id === selectedWhatsapp);
    if (!Array.isArray(whatsapp?.Queues)) return [];

    return whatsapp.Queues.filter(
      q => q.name?.toUpperCase() !== "FILA_CHATBOT"
    );
  }, [selectedWhatsapp, whatsapps]);

  const handleClose = () => {
    onClose();
    setChatbot(initialState);
    setSelectedWhatsapp(null);
    setIsSubmitting(false);
  };

  const handleSaveChatbot = async () => {

    if (!chatbot?.name || String(chatbot.name).trim() == "")  return toast.error('O nome é obrigatório.');
    if (!chatbot?.isReceptive)  return toast.error('O tipo de atendimento é obrigatório.');
    if (isSupportUser && !chatbot?.typeIntegration)  return toast.error('O tipo é obrigatório.');
    if (["1", "2"].includes(chatbot?.typeIntegration) && !chatbot?.integrationUrl) return toast.error('A URL de integração é obrigatória.');

    if (!selectedWhatsapp) return toast.error('Defina uma conexão de whatsapp para criar o ChatBot');

    // For non-support users, force typeIntegration to 0 (Construtor padrão)
    const chatbotData = isSupportUser
      ? { ...chatbot, whatsappId: selectedWhatsapp }
      : { ...chatbot, whatsappId: selectedWhatsapp, typeIntegration: 0 };

    if (chatbotData.name.length < 3) return toast.error('Insira um nome maior que 3 dígitos para o Chatbot');

    // Validação para URL quando for N8N ou Active Pieces
    if ((chatbotData.typeIntegration === 1 || chatbotData.typeIntegration === 2) && !chatbotData.integrationUrl) {
      return toast.error('A URL de integração é obrigatória para este tipo');
    }

    // Validação para inatividade
    if (chatbotData.inactivityMinutes >= 1) {
      if (["finish", "transfer"].includes(chatbotData.inactivityAction) == false) return toast.error("Selecione uma ação a ser realizada quando ocorrer inatividade.");
      if (chatbotData.inactivityAction == "transfer" && !chatbotData.inactivityQueueId) return toast.error("Selecione uma fila para transferir atendimentos com inatividade.");
      if (chatbotData.inactivityAction == "finish" && !chatbotData.inactivityConclusionId) return toast.error("Selecione uma finalização para atendimentos com inatividade.");
    }

    setIsSubmitting(true);

    try {
      if (chatbotId) {
        await api.put(`/chatbot/${chatbotId}`, chatbotData);
        toast.success('Atualizado com sucesso!');
        handleClose();
      } else {
        await api.post("/chatbot", chatbotData);
        toast.success('Cadastrado com sucesso!');
        handleClose();
      }
    } catch (err) {
      console.error({ err });
      toastError(err);
    }
  };

  const showUrlField = chatbot.typeIntegration === 1 || chatbot.typeIntegration === 2;

  const renderConnectionOptions = () => {
    const response = [];
    if (Array.isArray(whatsapps)) {
      whatsapps.map((w) => {
        response.push({
          key: w.id,
          value: w.name,
          disabled: false
        });
      });
    }
    return response;
  }

  return (
    <Dialog
      open={open == true ? true : false}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle id="form-dialog-title">
        {chatbotId ? 'Editar Chatbot' : 'Cadastrar Chatbot'}
      </DialogTitle>
      <DialogContent dividers>

        <Grid container spacing={2} mb={2}>
          <Grid item xs={12}>
            <Box
              style={{ marginBottom: "10px" }}
            >
              <Typography
                style={{ display: "inline-block" }}
              >
                Nome
              </Typography>
              <Typography
                component="span"
                style={{ marginLeft: 5, lineHeight: "normal" }}
                color="error"
              >
                *
              </Typography>
            </Box>
            <TextField
              name="chatbotName"
              id="chatbotName"
              fullWidth
              variant="outlined"
              placeholder="Insira o nome do Chatbot"
              inputProps={{ maxLength: 100 }}
              onChange={e =>
                setChatbot({ ...chatbot, name: e.target.value })
              }
              value={chatbot.name}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} mb={2}>
          <Grid item xs={12}>
            <SingleSelect
              title={
                <Typography>
                  Conexão
                  <Typography component="span" style={{ marginLeft: '5px' }} color="secondary">*</Typography>
                </Typography>
              }
              displayText={"selecionar conexão"}
              name="chatbotConnectionId"
              key="chatbotConnectionId"
              options={renderConnectionOptions()}
              onSelected={(opt) => setSelectedWhatsapp(opt)}
              selected={selectedWhatsapp}
              closeOnSelect={true}
              disabled={Boolean(chatbotId)}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} mb={2}>
          <Grid item xs={12}>
            <SingleSelect
              title={
                <Typography>
                  Tipo de Atendimento
                  <Typography component="span" style={{ marginLeft: '5px' }} color="secondary">*</Typography>
                </Typography>
              }
              displayText={"selecionar tipo de atendimento"}
              name="chatbotIsReceptive"
              key="chatbotIsReceptive"
              options={types.map((t) => { return { key: t.value, value: t.name, disabled: false } })}
              onSelected={(opt) =>
                setChatbot(cht => ({
                  ...cht,
                  isReceptive: opt,
                }))
              }
              selected={chatbot.isReceptive}
              closeOnSelect={true}
              disabled={Boolean(chatbotId)}
            />
          </Grid>
        </Grid>

        {isSupportUser && (
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12}>
              <SingleSelect
                title={
                  <Typography>
                    Tipo
                  </Typography>
                }
                displayText={"selecionar tipo"}
                name="chatbotTypeIntegration"
                key="chatbotTypeIntegration"
                options={integrationTypes.map((t) => { return { key: t.value, value: t.name, disabled: false } })}
                onSelected={(opt) =>
                  setChatbot(cht => ({
                    ...cht,
                    typeIntegration: opt,
                  }))
                }
                selected={chatbot.typeIntegration}
                closeOnSelect={true}
                disabled={Boolean(chatbotId)}
              />
            </Grid>
          </Grid>
        )}

        {showUrlField && (
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12}>
              <Box
                style={{ marginBottom: "10px" }}
              >
                <Typography
                  style={{ display: "inline-block" }}
                >
                  URL de Integração
                </Typography>
              </Box>
              <TextField
                name="chatbotIntegrationUrl"
                id="chatbochatbotIntegrationUrltName"
                fullWidth
                variant="outlined"
                placeholder="Insira a URL de integração do Chatbot"
                inputProps={{ maxLength: 255 }}
                onChange={e =>
                  setChatbot({ ...chatbot, integrationUrl: e.target.value })
                }
                value={chatbot.integrationUrl}
              />
            </Grid>
          </Grid>
        )}

        <Grid container spacing={2} mb={2}>
          <Grid item xs={12}>
            <Box
              style={{ marginBottom: "10px" }}
            >
              <Typography
                style={{ display: "inline-block" }}
              >
                Tempo de Inatividade (min: 1 minuto, max: 4320 minutos / 72 horas)
              </Typography>
            </Box>
            <TextField
              name="chatbotInactivityMinutes"
              id="chatbotInactivityMinutes"
              fullWidth
              variant="outlined"
              placeholder="Insira o tempo de inatividade em minutos"
              onChange={(e) => {
                const rawValue = e.target.value;

                // Permitir campo vazio
                if (rawValue === "") {
                  setChatbot({
                    ...chatbot,
                    inactivityMinutes: ""
                  });
                  return;
                }

                // Manter apenas números
                const numericValue = Number(rawValue.replace(/\D/g, ""));

                if (Number.isNaN(numericValue)) return;

                let finalValue = numericValue;

                if (numericValue < 1) finalValue = 1;
                else if (numericValue > 4320) finalValue = 4320;

                setChatbot({
                  ...chatbot,
                  inactivityMinutes: finalValue
                });
              }}
              value={chatbot.inactivityMinutes}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} mb={2} hidden={chatbot.inactivityMinutes == ""}>
          <Grid item xs={12}>
            <Box
              style={{ marginBottom: "10px" }}
            >
              <Typography
                style={{ display: "inline-block" }}
              >
                Mensagem de Anotação
              </Typography>
            </Box>
            <TextField
              name="chatbotInactivityAnnotationMessage"
              id="chatbotInactivityAnnotationMessage"
              fullWidth
              variant="outlined"
              placeholder="Insira o nome do Chatbot"
              inputProps={{ maxLength: 255 }}
              onChange={e =>
                setChatbot({ ...chatbot, inactivityAnnotationMessage: e.target.value })
              }
              value={chatbot.inactivityAnnotationMessage}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} mb={2} hidden={chatbot.inactivityMinutes == ""}>
          <Grid item xs={12}>
            <SingleSelect
              title={
                <Typography>
                  Ação para Inatividade
                  <Typography component="span" style={{ marginLeft: '5px' }} color="secondary">*</Typography>
                </Typography>
              }
              displayText={"selecionar ação"}
              name="chatbotInactivityAction"
              key="chatbotInactivityAction"
              options={[{ key: "finish", name: "Finalizar" }, { key: "transfer", name: "Transferir" }].map((t) => { return { key: t.key, value: t.name, disabled: false } })}
              onSelected={(opt) => setChatbot({ ...chatbot, inactivityAction: opt })}
              selected={chatbot.inactivityAction}
              closeOnSelect={true}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} mb={2} hidden={chatbot.inactivityMinutes == "" || chatbot.inactivityAction != "transfer"}>
          <Grid item xs={12}>
            <SingleSelect
              title={
                <Typography>
                  Fila para Transferência
                  <Typography component="span" style={{ marginLeft: '5px' }} color="secondary">*</Typography>
                </Typography>
              }
              displayText={"Selecionar Fila"}
              name="chatbotInactivityQueueId"
              key="chatbotInactivityQueueId"
              options={Array.isArray(connectionQueues) ? connectionQueues.map((c) => { return { key: c.id, value: c.name, disabled: false } }) : []}
              onSelected={(opt) => setChatbot({ ...chatbot, inactivityQueueId: opt })}
              selected={chatbot.inactivityQueueId}
              closeOnSelect={true}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} mb={2} hidden={chatbot.inactivityMinutes == "" || chatbot.inactivityAction != "finish"}>
          <Grid item xs={12}>
            <SingleSelect
              title={
                <Typography>
                  Finalização
                  <Typography component="span" style={{ marginLeft: '5px' }} color="secondary">*</Typography>
                </Typography>
              }
              displayText={"Selecionar Finalização"}
              name="chatbotInactivityConclusionId"
              key="chatbotInactivityConclusionId"
              options={Array.isArray(conclusionMessages) ? conclusionMessages.map((c) => { return { key: c.id, value: c.shortcut, disabled: false } }) : []}
              onSelected={(opt) => setChatbot({ ...chatbot, inactivityConclusionId: opt })}
              selected={chatbot.inactivityConclusionId}
              closeOnSelect={true}
            />
          </Grid>
        </Grid>

      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color="secondary"
          disabled={isSubmitting}
          variant="outlined"
        >
          {'Cancelar'}
        </Button>
        <Button
          type="submit"
          color="primary"
          disabled={isSubmitting}
          variant="contained"
          className={classes.btnWrapper}
          onClick={handleSaveChatbot}
        >
          {chatbotId
            ? `${'Salvar'}`
            : `${'Cadastrar'}`}
          {isSubmitting && (
            <CircularProgress
              size={24}
              className={classes.buttonProgress}
            />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChatbotModal;